import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, MenuItem, TextField } from '@material-ui/core';
import ReloadButton from 'CMFW/button/ReloadButton';
import ModuleContext from 'CMFW/context/ModuleContext';
import Grid from 'CMFW/grid/Grid';
import { ContainerLeft, ContainerLeftFooter, ContainerRight, ModuleContainer } from 'components/common/styles';
import { Split } from 'components/Split';
import { useForceRender } from 'hooks/useForceRender';
import { get } from 'services/request';
import { IFields } from 'CMFW/grid/fields';
import { asyncTaskStatusRenderer } from 'CMFW/grid/cellRenderers/asyncTaskStatus';
import TranslationBatchJobModel from 'models/TranslationBatchJobModel';
import TranslationBatchModel, { IBatchJob, BatchJobStatus } from 'models/TranslationBatchModel';
import TranslationProjectTranslationItemsModel from 'models/TranslationProjectTranslationItemsModel';
import { IUnifiedDialect, IUnifiedLanguage } from 'models/TranslationUnifiedDashboardModel';
import UnifiedBatchDetails from '../components/UnifiedBatchDetails';
import UnifiedBatchCreate from '../components/UnifiedBatchCreate';

interface IDialectsResponse {
  dialects: IUnifiedDialect[];
  languages: IUnifiedLanguage[];
}

type RightPanel = 'create' | 'detail';

const UnifiedBatch: React.FC = () => {
  const forceRender = useForceRender();

  const gridModel = useMemo(() => new TranslationBatchJobModel({ render: forceRender }), [forceRender]);
  const batchModel = useMemo(() => new TranslationBatchModel({ render: forceRender }), [forceRender]);
  const itemsModel = useMemo(() => new TranslationProjectTranslationItemsModel({ render: forceRender }), [forceRender]);

  const [dialects, setDialects] = useState<IUnifiedDialect[]>([]);
  const [listDialectId, setListDialectId] = useState(0);
  const [rightPanel, setRightPanel] = useState<RightPanel>('create');

  // Load dialects
  useEffect(() => {
    (async () => {
      const result = await get<IDialectsResponse>('/translation-unified/dialects');
      setDialects(result.dialects);
    })();
  }, []);

  // Load grid when dialect changes
  useEffect(() => {
    if (listDialectId > 0) {
      gridModel.setDialectId(listDialectId);
      gridModel.getGrid();
      batchModel.startPolling(listDialectId);
    }
    return () => batchModel.stopPolling();
  }, [listDialectId, gridModel, batchModel]);

  // Auto-refresh grid when polling updates jobs
  useEffect(() => {
    if (batchModel.jobs.length > 0 && listDialectId > 0) {
      gridModel.gridData = batchModel.jobs;
      gridModel.loadingGrid = false;
      forceRender();
    }
  }, [batchModel.jobs]);

  const handleListDialectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setListDialectId(parseInt(e.target.value));
  };

  const handleShowCreate = () => {
    setRightPanel('create');
    gridModel.itemId = gridModel.NEW_CREATE_ID;
    forceRender();
  };

  const handleBatchCreated = (dialectId: number) => {
    setListDialectId(dialectId);
    gridModel.setDialectId(dialectId);
    gridModel.getGrid();
    setRightPanel('create');
  };

  // When grid item is selected, show details
  useEffect(() => {
    if (gridModel.itemId > 0 && gridModel.itemId !== gridModel.NEW_CREATE_ID) {
      setRightPanel('detail');
    }
  }, [gridModel.itemId]);

  // Group dialects by language
  const dialectsByLanguage: { [lang: string]: IUnifiedDialect[] } = {};
  dialects.forEach((d) => {
    if (!dialectsByLanguage[d.languageName]) {
      dialectsByLanguage[d.languageName] = [];
    }
    dialectsByLanguage[d.languageName].push(d);
  });

  const selectedJob: IBatchJob | null =
    rightPanel === 'detail' && gridModel.itemId > 0
      ? gridModel.gridData.find((j) => j.id === gridModel.itemId) ?? null
      : null;

  const cellRenderers = useMemo(
    () => ({
      [IFields.async_task_status]: asyncTaskStatusRenderer,
    }),
    [],
  );

  return (
    <ModuleContainer>
      <ModuleContext.Provider value={gridModel}>
        <Split widthLeft={500}>
          <ContainerLeft>
            <SelectsWrapper>
              <SelectField
                select
                value={listDialectId}
                variant="outlined"
                label="Source Dialect"
                onChange={handleListDialectChange}
                size="small"
              >
                <MenuItem value={0}>Select dialect...</MenuItem>
                {Object.keys(dialectsByLanguage)
                  .sort()
                  .map((lang) =>
                    dialectsByLanguage[lang].map((d) => (
                      <MenuItem key={d.dialectId} value={d.dialectId}>
                        {d.languageName} - {d.dialectName} ({d.dialectCode})
                      </MenuItem>
                    )),
                  )}
              </SelectField>
            </SelectsWrapper>
            <Grid cellRenderers={cellRenderers} />
            <ContainerLeftFooter>
              <ReloadButton />
              <Button variant="outlined" size="small" onClick={handleShowCreate}>
                new
              </Button>
            </ContainerLeftFooter>
          </ContainerLeft>

          <ContainerRight>
            {rightPanel === 'create' ? (
              <UnifiedBatchCreate
                batchModel={batchModel}
                onCreated={handleBatchCreated}
                onCancel={() => setRightPanel('detail')}
              />
            ) : selectedJob ? (
              <UnifiedBatchDetails
                job={selectedJob}
                batchModel={batchModel}
                itemsModel={itemsModel}
                forceRender={forceRender}
                dialectId={listDialectId}
              />
            ) : (
              <EmptyPanel>Select a bulk job to view details</EmptyPanel>
            )}
          </ContainerRight>
        </Split>
      </ModuleContext.Provider>
    </ModuleContainer>
  );
};

export default UnifiedBatch;

const SelectsWrapper = styled.div`
  padding: 8px 10px;
  display: flex;
  gap: 10px;
`;

const SelectField = styled(TextField)`
  flex: 1;
  min-width: 160px;
`;

const EmptyPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  font-size: 14px;
`;
